// src/hooks/useFunctions.ts
import React, { useState, useCallback } from 'react';
import { connectFunctionsEmulator, getFunctions, httpsCallable } from 'firebase/functions';
import { useTranslation } from 'react-i18next';
import showNotification from '../components/extras/showNotification';
import Icon from '../components/icon/Icon';

interface CallFunctionOptions {
	showErrorNotification?: boolean;
}

/**
 * Custom hook for calling Firebase Functions with automatic language inclusion
 */
export default function useFunctions() {
	const [loading, setLoading] = useState<boolean>(false);
	const [error, setError] = useState<any>(null);
	const { t, i18n } = useTranslation('translation');

	const reset = useCallback(() => {
		setError(null);
		setLoading(false);
	}, []);

	const callFunction = useCallback(
		async <T,>(
			functionName: string,
			params: any = {},
			options: CallFunctionOptions = { showErrorNotification: true },
		): Promise<T | null> => {
			try {
				setLoading(true);
				setError(null);

				// Get functions instance
				const functions = getFunctions();
				// Add language to params
				const enhancedParams = {
					...params,
					language: i18n.language,
				};

				// Call the Firebase function
				const functionRef = httpsCallable(functions, functionName);
				const result = await functionRef(enhancedParams);

				// Type the result
				const typedResult = result.data as T;

				setLoading(false);
				return typedResult;
			} catch (err: any) {
				setError(err);
				setLoading(false);

				// Show error notification if enabled
				if (options.showErrorNotification) {
					showNotification(
						<span className='d-flex align-items-center'>
							<Icon icon='Error' size='lg' className='me-1' />
							<span>{t('Error')}</span>
						</span>,
						err.message || t('ErrorOccurred'),
						'danger',
					);
				}

				return null;
			}
		},
		[i18n.language, t],
	);

	return { loading, error, callFunction, reset };
}
